import React, { useState, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { adminVerify, adminStats, adminListDemos, adminListEvents, adminListPartners, adminListContacts } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Lock, BarChart3, Loader2, RefreshCw, LogOut, Download } from "lucide-react";
import { exportToCSV } from "@/lib/csv";

const STORAGE_KEY = "bicenter_admin_key";

const Admin = () => {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setKey(stored);
      adminVerify(stored)
        .then(() => setAuthed(true))
        .catch(() => localStorage.removeItem(STORAGE_KEY));
    }
  }, []);

  const tryLogin = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await adminVerify(key);
      localStorage.setItem(STORAGE_KEY, key);
      setAuthed(true);
      toast.success("Welcome");
    } catch (err) {
      toast.error("Invalid admin key");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setKey("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none"></div>
        <div className="relative w-full max-w-md">
          <div className="text-center mb-8 flex justify-center">
            <Logo size="lg" />
          </div>
          <form onSubmit={tryLogin} className="bg-[#0c0c0e] border border-white/10 rounded-3xl p-8 space-y-5" data-testid="admin-login-form">
            <div className="flex items-center gap-2 text-zinc-400">
              <Lock size={16} />
              <p className="text-sm uppercase tracking-[0.25em]">Admin access</p>
            </div>
            <h1 className="font-display text-2xl text-white">BICENTER AI Dashboard</h1>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Admin key</Label>
              <Input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                required
                data-testid="admin-key-input"
                className="bg-white/[0.03] border-white/10 text-white h-12"
              />
            </div>
            <Button
              type="submit"
              disabled={verifying}
              data-testid="admin-login-button"
              className="w-full h-12 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white"
            >
              {verifying ? <Loader2 size={14} className="animate-spin" /> : "Access dashboard"}
            </Button>
          </form>
        </div>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  return <AdminDashboard apiKey={key} onLogout={logout} />;
};

const AdminDashboard = ({ apiKey, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [demos, setDemos] = useState([]);
  const [events, setEvents] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, d, e, p, c] = await Promise.all([
        adminStats(apiKey),
        adminListDemos(apiKey),
        adminListEvents(apiKey),
        adminListPartners(apiKey),
        adminListContacts(apiKey),
      ]);
      setStats(s);
      setDemos(d);
      setEvents(e);
      setPartners(p);
      setContacts(c);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A0A0A]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-3">
            <Button onClick={loadAll} size="sm" variant="outline" data-testid="admin-refresh" className="border-white/15 text-white hover:bg-white/5">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
            <Button onClick={onLogout} size="sm" variant="ghost" data-testid="admin-logout" className="text-zinc-400 hover:text-white">
              <LogOut size={14} />
              <span className="ml-2 hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="text-bicenter-cyan" size={20} />
          <h1 className="font-display text-3xl text-white">Leads Dashboard</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Demo bookings" value={stats?.demo_bookings ?? "—"} accent="cyan" />
          <StatCard label="Event registrations" value={stats?.event_registrations ?? "—"} accent="violet" />
          <StatCard label="Partner applications" value={stats?.partner_applications ?? "—"} accent="magenta" />
          <StatCard label="Contact messages" value={stats?.contact_messages ?? "—"} accent="blue" />
        </div>

        <Tabs defaultValue="demos">
          <TabsList className="bg-[#0c0c0e] border border-white/10 grid grid-cols-2 md:grid-cols-4 h-auto rounded-full p-1">
            <TabsTrigger value="demos" className="rounded-full data-[state=active]:bg-white/10 text-zinc-400 data-[state=active]:text-white">Demos</TabsTrigger>
            <TabsTrigger value="events" className="rounded-full data-[state=active]:bg-white/10 text-zinc-400 data-[state=active]:text-white">Events</TabsTrigger>
            <TabsTrigger value="partners" className="rounded-full data-[state=active]:bg-white/10 text-zinc-400 data-[state=active]:text-white">Partners</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full data-[state=active]:bg-white/10 text-zinc-400 data-[state=active]:text-white">Contacts</TabsTrigger>
          </TabsList>

          <TabsContent value="demos" className="mt-6">
            <LeadsTable
              testid="table-demos"
              items={demos}
              filename="bicenter_demo_bookings"
              fullColumns={[
                { k: "created_at", h: "Date" },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "company", h: "Company" },
                { k: "role", h: "Role" },
                { k: "phone", h: "Phone" },
                { k: "lob_solution", h: "LOB Solution" },
                { k: "preferred_date", h: "Preferred date" },
                { k: "message", h: "Message" },
                { k: "status", h: "Status" },
                { k: "id", h: "ID" },
              ]}
              columns={[
                { k: "created_at", h: "Date", fmt: fmtDate },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "company", h: "Company" },
                { k: "lob_solution", h: "LOB" },
                { k: "preferred_date", h: "Preferred date" },
              ]}
            />
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <LeadsTable
              testid="table-events"
              items={events}
              filename="bicenter_event_registrations"
              fullColumns={[
                { k: "created_at", h: "Date" },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "company", h: "Company" },
                { k: "role", h: "Role" },
                { k: "phone", h: "Phone" },
                { k: "event_id", h: "Event ID" },
                { k: "event_title", h: "Event title" },
                { k: "event_type", h: "Event type" },
                { k: "tier", h: "Tier" },
                { k: "message", h: "Message" },
                { k: "status", h: "Status" },
                { k: "id", h: "ID" },
              ]}
              columns={[
                { k: "created_at", h: "Date", fmt: fmtDate },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "event_title", h: "Event" },
                { k: "event_type", h: "Type" },
                { k: "tier", h: "Tier" },
              ]}
            />
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <LeadsTable
              testid="table-partners"
              items={partners}
              filename="bicenter_partner_applications"
              fullColumns={[
                { k: "created_at", h: "Date" },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "company", h: "Company" },
                { k: "role", h: "Role" },
                { k: "phone", h: "Phone" },
                { k: "application_type", h: "Application type" },
                { k: "website", h: "Website" },
                { k: "budget_range", h: "Budget range" },
                { k: "message", h: "Message" },
                { k: "status", h: "Status" },
                { k: "id", h: "ID" },
              ]}
              columns={[
                { k: "created_at", h: "Date", fmt: fmtDate },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "company", h: "Company" },
                { k: "application_type", h: "Type" },
                { k: "budget_range", h: "Budget" },
              ]}
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <LeadsTable
              testid="table-contacts"
              items={contacts}
              filename="bicenter_contact_messages"
              fullColumns={[
                { k: "created_at", h: "Date" },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "subject", h: "Subject" },
                { k: "message", h: "Message" },
                { k: "status", h: "Status" },
                { k: "id", h: "ID" },
              ]}
              columns={[
                { k: "created_at", h: "Date", fmt: fmtDate },
                { k: "name", h: "Name" },
                { k: "email", h: "Email" },
                { k: "subject", h: "Subject" },
                { k: "message", h: "Message", truncate: true },
              ]}
            />
          </TabsContent>
        </Tabs>
      </main>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
};

const StatCard = ({ label, value, accent }) => {
  const accents = {
    cyan: "from-bicenter-cyan/20 to-transparent",
    violet: "from-bicenter-violet/20 to-transparent",
    magenta: "from-bicenter-magenta/20 to-transparent",
    blue: "from-bicenter-blue/20 to-transparent",
  };
  return (
    <div className={`relative bg-gradient-to-br ${accents[accent]} border border-white/10 rounded-2xl p-6`}>
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">{label}</p>
      <p className="mt-3 font-display text-4xl text-white font-light">{value}</p>
    </div>
  );
};

const LeadsTable = ({ items, columns, testid, filename, fullColumns }) => {
  const handleExport = () => {
    const cols = fullColumns || columns;
    const ok = exportToCSV(items, cols, filename || "bicenter_export");
    if (ok) toast.success(`${items.length} ligne(s) exportée(s)`);
    else toast.error("Aucune donnée à exporter");
  };

  if (!items || items.length === 0) {
    return (
      <div className="border border-white/10 rounded-2xl p-12 text-center text-zinc-500" data-testid={testid}>
        No data yet.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">
          {items.length} lead{items.length > 1 ? "s" : ""}
        </p>
        <Button
          onClick={handleExport}
          size="sm"
          data-testid={`${testid}-export-csv`}
          className="bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white hover:shadow-[0_0_20px_rgba(0,169,255,0.35)]"
        >
          <Download size={14} className="mr-2" />
          Exporter CSV
        </Button>
      </div>
      <div className="border border-white/10 rounded-2xl overflow-hidden bg-[#0c0c0e]" data-testid={testid}>
        <div className="overflow-x-auto">
          <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.k} className="text-xs uppercase tracking-[0.2em] text-zinc-500">{c.h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row, i) => (
              <TableRow key={row.id || i} className="border-white/5 hover:bg-white/[0.02]">
                {columns.map((c) => {
                  const v = c.fmt ? c.fmt(row[c.k]) : (row[c.k] ?? "—");
                  return (
                    <TableCell key={c.k} className={`text-sm text-zinc-200 ${c.truncate ? "max-w-xs truncate" : ""}`}>
                      {v || "—"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </div>
    </div>
  );
};

export default Admin;
